@if (available()) {
  <section class="p-8" aria-label="Version history">
    <h2>Version history</h2>
    <p>Restoration creates a new version. Current ownership and access remain unchanged.</p>
    @if (failure()) {
      <p role="alert">{{ failure() }}</p>
      @if (!confirmation()) {
        <button type="button" [disabled]="busy()" (click)="retry()">Retry</button>
      }
    }
    @if (loading()) {
      <p role="status">Loading versions…</p>
    } @else if (!failure() && !rows().length) {
      <p>No versions yet</p>
    }
    <ul class="list-none p-0">
      @for (row of rows(); track row.version) {
        <li class="mb-6 rounded-lg border border-border p-6">
          <strong>Version {{ row.version }}</strong>
          @if (row.version === current()) {
            <strong>Current</strong>
          }
          <p>
            {{ row.author?.name ?? 'Former member' }} ·
            <time [attr.datetime]="row.at">{{ row.at | dateFormat: 'relativeDate' }}</time>
          </p>
          @if (row.restoredFrom !== null) {
            <p>Restored from version {{ row.restoredFrom }}</p>
          }
          @if (row.fields.length) {
            <p>Changed: {{ changed(row) }}</p>
          }
          @if (row.review) {
            <p>{{ review(row) }}</p>
          }
          @if (row.version !== current()) {
            <details (toggle)="compare(row.version, $event)">
              <summary>Compare with current version</summary>
              @switch (comparisonState(row.version)) {
                @case ('loading') {
                  <p role="status">Loading this version…</p>
                }
                @case ('failed') {
                  <p role="alert">{{ comparisonFailure(row.version) }}</p>
                }
                @case ('ready') {
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">Field</th>
                        <th scope="col">Version {{ row.version }}</th>
                        <th scope="col">Current version</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (field of comparisonRows(row.version); track field.label) {
                        <tr>
                          <th scope="row">{{ field.label }}</th>
                          @if (field.kind === 'date') {
                            <td>
                              {{
                                field.saved
                                  ? (field.saved
                                    | dateFormat: field.savedFormat : undefined : true : field.savedTimeZone)
                                  : '—'
                              }}
                            </td>
                            <td>
                              {{
                                field.current
                                  ? (field.current
                                    | dateFormat: field.currentFormat : undefined : true : field.currentTimeZone)
                                  : '—'
                              }}
                            </td>
                          } @else {
                            <td>{{ field.saved ?? '—' }}</td>
                            <td>{{ field.current ?? '—' }}</td>
                          }
                        </tr>
                      }
                    </tbody>
                  </table>
                }
              }
            </details>
          }
          @if (canEdit() && row.version !== current()) {
            <button type="button" data-restore [disabled]="busy()" (click)="askRestore(row.version)">
              Restore content
            </button>
          }
          @if (confirmation() === row.version) {
            <section aria-label="Confirm restore">
              <p>
                Only the title, description, priority, dates and recurrence will change. Completion, assignee, project,
                ownership and access stay the same.
              </p>
              @if (held()) {
                <p role="status">Save or cancel your changes first.</p>
              }
              <button type="button" #restoreCancel [disabled]="busy()" (click)="cancel()">Cancel</button>
              <button type="button" [disabled]="busy() || held() || !canEdit()" (click)="confirmRestore()">
                Confirm restore
              </button>
            </section>
          }
        </li>
      }
    </ul>
    @if (total() > 25 || page()) {
      <nav aria-label="Version pages">
        <button type="button" [disabled]="page() === 0 || loading() || busy()" (click)="previous()">Previous</button>
        <button type="button" [disabled]="(page() + 1) * 25 >= total() || loading() || busy()" (click)="next()">
          Next
        </button>
      </nav>
    }
  </section>
}
